import {Action, ActionCreator, isAnyOf, isRejected} from '@reduxjs/toolkit';
import * as Function from 'effect/Function';
import * as Predicate from 'effect/Predicate';
import {initUserinfoEffects, selectIsUserOnBoard, selectUserIsAuthorizedFlag, userinfoRoutine} from '~/entities/user';
import {historyUpdate} from '~/library/navigation';
import {Argument} from '~/library/types';
import {
    AccountDashboardCategory,
    InstrumentDashboardCategory,
    refreshAccounts as refreshAccountsAction,
    refreshHomePage,
    refreshInstruments as refreshInstrumentsAction,
} from '~/processes/dashboard';
import appActions from '~/redux/appActions';
import {assetsRequestRoutine} from '~/redux/assets/assetsActions';
import {balancesRoutine} from '~/redux/balances/balancesRoutine';
import {onlyLeading} from '~/shared/utils/for-redux';
import initAssetsDefault from './assets/assetsEffect';
import initBalancesEffect from './balances/balancesEffect';
import initDynamicRateEffect from './dynamicRate/dynamicRateEffect';
import initFinancesEffects, {operationsRequestRoutine} from './finances/financesEffects';

const initErrorHandling = (startListening: AppStartListening) => {
    function hasError(action: Action): action is Action & {error: {stack: string; message: string}} {
        return (
            'error' in action &&
            'object' === typeof action.error &&
            null !== action.error &&
            'stack' in action.error &&
            'message' in action.error
        );
    }

    const isErrorAction = isAnyOf(isRejected, hasError);

    startListening({
        matcher: isErrorAction,
        effect(action) {
            if (!import.meta.env.PROD) {
                console.log(`%c ${action.type}`, 'color:red');
            }
            if ('meta' in action && action.meta.rejectedWithValue) {
                throw action.payload;
            }
            if (action.error instanceof Error) {
                throw action.error;
            } else {
                throw Object.assign(new Error(action.error.message), {stack: action.error.stack});
            }
        },
    });
};

const initDataRefreshEffect = (startListening: AppStartListening) => {
    type Matcher = Argument<typeof isAnyOf>;
    type ListenerPredicate = Predicate.Predicate<[action: Action, currentState: AppModel, originalState: AppModel]>;
    type ListenerPredicateFactory<I extends any[]> = (...args: I) => ListenerPredicate;

    const actionIsAnyOf: ListenerPredicateFactory<Matcher[]> = (...matchers) => {
        const match = isAnyOf(...matchers);
        return ([action]) => match(action);
    };

    const userIsAuthorized: ListenerPredicate = ([, state]) => selectUserIsAuthorizedFlag(state);
    const userIsOnBoard: ListenerPredicate = ([, state]) => selectIsUserOnBoard(state);

    const omniRefresh = isAnyOf(appActions.refreshData, refreshHomePage, historyUpdate.isTo('/assets'));

    const shouldRefreshAccounts = actionIsAnyOf(
        omniRefresh,
        refreshAccountsAction,
        historyUpdate.isTo('/assets/:category{/:id}', {
            category: AccountDashboardCategory.isAssetType,
        })
    );

    const shouldRefreshInstruments = actionIsAnyOf(
        omniRefresh,
        refreshInstrumentsAction,
        historyUpdate.isTo('/assets/:category{/:id}', {
            category: InstrumentDashboardCategory.isAssetType,
        })
    );

    const globalRoutines: {
        trigger: ActionCreator<Action>;
        success: Matcher;
        failure: Matcher;
        aborted: Matcher;
        shouldRun: ListenerPredicate;
    }[] = [
        {
            ...userinfoRoutine,
            shouldRun: Predicate.and(actionIsAnyOf(omniRefresh), userIsAuthorized),
        },
        {
            ...assetsRequestRoutine,
            shouldRun: Predicate.and(Predicate.or(shouldRefreshAccounts, shouldRefreshInstruments), userIsAuthorized),
        },
        {
            ...operationsRequestRoutine,
            shouldRun: Predicate.every([shouldRefreshAccounts, userIsAuthorized, userIsOnBoard]),
        },
        {
            ...balancesRoutine,
            shouldRun: Predicate.every([shouldRefreshInstruments, userIsAuthorized, userIsOnBoard]),
        },
    ];

    startListening({
        predicate: Function.untupled(Predicate.some(globalRoutines.map(r => r.shouldRun))),
        effect: onlyLeading(async (action, api) => {
            const routinesToRun = globalRoutines.filter(routine =>
                routine.shouldRun([action, api.getState(), api.getOriginalState()])
            );
            const finishedActions = routinesToRun.map(routine =>
                api.condition(isAnyOf(routine.success, routine.failure, routine.aborted))
            );
            for (const routine of routinesToRun) {
                api.dispatch(routine.trigger());
            }
            await Promise.all(finishedActions);
        }),
    });
};

export const effectsInits = [
    initUserinfoEffects,
    initErrorHandling,
    initFinancesEffects,
    initDataRefreshEffect,
    initBalancesEffect,
    initDynamicRateEffect,
    initAssetsDefault,
];
